import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getQueryBoundary } from '@confluence/query-preloader-tools/entry-points/queryBoundaryStorage.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { isLocal as isLocalPillar } from '@confluence/ssr-utilities/entry-points/env.ts';

import { fetchORSForSSR } from '../entry-points/fetchORSForSSR.ts';
import { getInitialState } from '../entry-points/getInitialState.ts';
import { getSmartLinks } from '../entry-points/getSmartLinks.ts';

import {
	buildResolvedSmartLinkState,
	partitionResolvedLinks,
} from './buildResolvedSmartLinkState.ts';
import type {
	preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQuery$data,
	preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQuery$variables,
} from './__generated__/preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQuery.graphql.ts';
import preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQueryArtifact from './__generated__/preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQuery.graphql.ts';
import {
	groupSmartLinksByHostname,
	mergeSmallThirdPartyBatches,
	splitLinksIntoBatches,
	getLinkType,
} from './smartLinkBatchHelpers.ts';
import type { SmartLinkType } from './getSmartLinks.ts';

declare global {
	interface Window {
		__SSR_SMARTLINKS__?: { [key: string]: any } | {};
		__SSR_SMARTLINKS_TITLES__?: { [url: string]: string };
	}
}

export const SMART_LINK_INDIVIDUAL_TIMEOUT = 250;
export const SMART_LINK_BATCH_TIMEOUT = 350;
export const DEFAULT_MIN_BATCH_COUNT = 1;
export const DEFAULT_MAX_BATCH_COUNT = 50;

export const preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQuery: GraphQLTaggedNode = graphql`
	query preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQuery(
		$contentId: ID!
		$smartLinksFromDraft: Boolean = false
	) {
		contentSmartLinks(id: $contentId) @skip(if: $smartLinksFromDraft) {
			nodes {
				__typename
				url
				title
				emojiTitle
				canView
				product
				resourceType
				subType
				iconUrl
				resolved
				statusText
				statusAppearance
			}
		}
		confluence_contentSmartLinksForDraft(id: $contentId) @include(if: $smartLinksFromDraft) {
			nodes {
				__typename
				url
				title
				emojiTitle
				canView
				product
				resourceType
				subType
				iconUrl
				resolved
				statusText
				statusAppearance
			}
		}
	}
`;

const getContentSmartLinksORSMeasureName = () => {
	const queryBoundary = getQueryBoundary();

	if (!queryBoundary || queryBoundary === 'default') {
		return 'ssr-app/prepare/preloadQuery/fetch:contentSmartLinksORS';
	}

	return `ssr-app/prepare/preloadQuery/dataFetcher:${queryBoundary}/fetch:contentSmartLinksORS`;
};

export const preloadContentSmartlinksQuery = async (
	contentId: string,
	isDraft: boolean = false,
): Promise<SmartLinkType[]> => {
	const smartLinksRes = await query<
		preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQuery$data,
		preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQuery$variables
	>({
		query: preloadContentSmartLinksViewPageExtensionsAndRendererAssetsQueryArtifact,
		variables: {
			contentId,
			smartLinksFromDraft: isDraft,
		},
	});
	return getSmartLinks(smartLinksRes, isDraft);
};

export const getSmartLinksStats = (
	orsCalls: any[],
	smartLinks: any[],
	resolvedSmartLinks: any[],
	fetchTimeout: Record<string, number>,
): {
	fetch: number;
	fetchTimeout: Record<string, number>;
	links: any;
} => {
	const resolvedLinks = resolvedSmartLinks.reduce((acc, { status, body }) => {
		if (status === 504) {
			acc[body?.data?.url] = 'timeout';
		}
		if (status === 200) {
			acc[body?.data?.url] = 'resolved';
		}
		return acc;
	}, {});
	return {
		fetch: orsCalls.length,
		fetchTimeout,
		links: smartLinks.reduce((acc, { resourceUrl, appearance, product }) => {
			const linkType = product || getLinkType(resourceUrl);
			if (!acc?.[linkType]?.[appearance]) {
				acc[linkType] = { [appearance]: { total: 0, timeout: 0, resolved: 0 } };
			}
			acc[linkType][appearance].total++;
			if (resolvedLinks[resourceUrl]) {
				if (resolvedLinks[resourceUrl] === 'timeout') {
					acc[linkType][appearance].timeout++;
				}
				if (resolvedLinks[resourceUrl] === 'resolved') {
					acc[linkType][appearance].resolved++;
				}
			}
			return acc;
		}, {}),
	};
};

export async function fetchORSForSSRWithTimeout(
	links: SmartLinkType[],
	timeout: number,
	batchTimeout: number,
	fetchTimeout: Record<string, number>,
	shouldSkipTimeGuard?: boolean,
): Promise<any> {
	if (links.length === 0) {
		return [];
	}

	const { customURLs, userContext } = getSSRRenderInputs();
	const objectResolverURL = isLocalPillar()
		? '/gateway/api/object-resolver/resolve/batch'
		: customURLs?.['object-resolver-service'];

	const firstLink = links[0].resourceUrl;
	const type = getLinkType(firstLink);

	const task = async () => {
		try {
			// ORS only expects resourceURL and appearance, otherwise it throws a 400 for any unrecognized fields
			// GQL can return any number of fields (title, emoji, resolved state, etc) but ORS should only accept
			// resourceURL and appearance
			const sanitizedLinksForORS = links.map(({ resourceUrl, appearance }) => ({
				resourceUrl,
				appearance,
			}));
			const res = await fetchORSForSSR({
				// Despite the API has a timeout it does not respect it. It can easily go over time.
				url: `${objectResolverURL}?timeout=${timeout}`,
				body: JSON.stringify(sanitizedLinksForORS),
				userContext,
			});
			if (!res.ok) {
				throw new Error(`${res.status} ${res.statusText}`);
			}
			return res.json();
		} catch (e) {
			ssrPrepareErrorsClient.submitError(
				new Error(`Smart link content ORS call failed: ${e.message}`),
			);
		}
		return [];
	};

	if (shouldSkipTimeGuard) {
		return task();
	}

	return timeGuard(
		task,
		() => {
			fetchTimeout[type] = fetchTimeout[type] || 0;
			fetchTimeout[type]++;
			return [];
		},
		batchTimeout,
	);
}

/*
 * preloadContentSmartLinks tracks the entire preloaded chain of queries needed to SSR Content Smart Links. This consists of two calls:
 * 1. preloadContentSmartlinksQuery, a GQL call to fetch SmartLinks URLs and
 * 2. A call to ORS to resolve the metadata, which uses the links returned from the previous call
 * The entirety of this chain is currently best effort, which means that SmartLinks will either resolve ALL
 * links by the time the longest preloaded query resolves or NONE.
 *
 * If a timeout is provided, Smartlinks will try and resolve as many links as possible
 * within the provided timeout.
 */

export const preloadContentSmartLinks = async (
	contentId: string,
	isDraft: boolean = false,
): Promise<void> => {
	const smartLinks = await preloadContentSmartlinksQuery(contentId, isDraft);
	if (!smartLinks?.length) {
		return;
	}

	// How long a single ORS resolution can take
	const timeout = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_max_time',
		SMART_LINK_INDIVIDUAL_TIMEOUT,
	);

	// How long a batch of ORS resolution can take
	const batchTimeout = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_batch_max_time',
		SMART_LINK_BATCH_TIMEOUT,
	);

	// How many links should be packed in a batch
	const min = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_min_batch_count',
		DEFAULT_MIN_BATCH_COUNT,
	);
	const max = Math.max(
		min,
		StatsigConfigurations.getNumber(
			'confluence_ssr_content_smart_link_max_batch_count',
			DEFAULT_MAX_BATCH_COUNT,
		),
	);

	const { resolvedFromGql, linksForORS } = partitionResolvedLinks(smartLinks);
	// format resolved links to match that of getInitialState
	const gqlResolvedState = buildResolvedSmartLinkState(resolvedFromGql);

	const gqlStats = {
		totalLinks: smartLinks.length,
		resolvedFromGqlCount: resolvedFromGql?.length,
		resolvedJiraFromGqlCount: resolvedFromGql?.filter((link) => link.product === 'jira').length,
		linksForORSCount: linksForORS?.length,
		skippedORS: linksForORS?.length === 0,
	};

	const orsCalls: Promise<any>[] = [];
	const fetchTimeout = {};
	let resolvedSmartLinks: any[] = [];

	if (linksForORS.length > 0) {
		await SSRMeasures.run(getContentSmartLinksORSMeasureName(), async () => {
			let smartLinksBatches = groupSmartLinksByHostname(linksForORS);
			smartLinksBatches = mergeSmallThirdPartyBatches(smartLinksBatches, min);
			smartLinksBatches = splitLinksIntoBatches(smartLinksBatches, max);

			for (const smartLinksBatch of smartLinksBatches) {
				orsCalls.push(
					fetchORSForSSRWithTimeout(smartLinksBatch, timeout, batchTimeout, fetchTimeout),
				);
			}

			// eslint-disable-next-line check-preloaders/no-promise-all
			resolvedSmartLinks = (await Promise.all(orsCalls)).flat();
		});
	}

	const titles: Record<string, string> = {};
	for (const link of linksForORS) {
		if (link.title) {
			titles[link.resourceUrl] = link.title;
		}
	}
	if (Object.keys(titles).length > 0) {
		window['__SSR_SMARTLINKS_TITLES__'] = titles;
	}

	// Format response to fit SmartCard Provider storeOptions initial state
	// Save correctly formatted response to window obj so it can be used on SSR and SPA (no duplicate calls)
	// Merge GraphQL-resolved state with ORS-resolved state
	window['__SSR_SMARTLINKS__'] = { ...gqlResolvedState, ...getInitialState(resolvedSmartLinks) };

	window['__SSR_SMARTLINKS_STATS__'] = {
		...getSmartLinksStats(orsCalls, smartLinks, resolvedSmartLinks, fetchTimeout),
		gql: gqlStats,
	};
};
